import React, { useState, useEffect } from 'react';
import {
  Backdrop,
  Container,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Link, withRouter } from 'react-router-dom';
import { Button, BoxV2 as Box, Flex, Table, useTable } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faBullhorn } from '@fortawesome/pro-regular-svg-icons';

import { Loader } from '../../../../shared_elements';
import { BrandSuspensionModal, SuspensionCampaignRow } from '../components';
import Spinner from '../../Campaigns/components/Spinner';
import { MNO_STATUS_ENUM } from '../../../../constants';
import { toastFlashMessage } from '../../../../utils';
import {
  fetchBrandDetail,
  getSuspensionRule,
  getSuspensionPreviewCampaigns,
  createSuspensionRule,
  updateSuspensionRule,
} from '../apiServices';

const headRows = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'usecase', label: 'USE-CASE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'operationStatus', label: 'MNO STATUS', sortable: false },
];

const useStyles = makeStyles(() => ({
  progressBackground: {
    background: 'rgba(14, 31, 43, 0.75)',
    zIndex: 1005,
  },
  progressModal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '430px',
    height: '226px',
    background: '#FFFFFF',
    borderRadius: '5px',
    marginLeft: 'calc(50% - 120px)',
  },
  progressText: {
    textAlign: 'center',
    width: '100%',
    marginTop: 30,
    color: '#49535D',
    wordBreak: 'break-all',
  },
}));

const BrandSuspension = (props) => {
  const classes = useStyles();
  const { location, history } = props;
  const brandUid = props.match.params.id;
  const [loader, setLoader] = useState(true);
  const [brandSuspended, setBrandSuspended] = useState(null);
  const [brandInfo, setBrandInfo] = useState({});
  const [effectedCampaignCount, setEffectedCampaignCount] = useState(0);
  const [suspensionRuleId, setSuspensionRuleId] = useState(null);
  const [openBrandSuspension, setOpenBrandSuspension] = useState(false);
  const [progressMsg, setProgressMsg] = useState(null);

  const table = useTable(
    async (tableFilter) => {
      const query = {
        ...tableFilter,
        brandUid,
        channelId: '10DLC',
        recordsPerPage: 10,
      };
      const response = await getSuspensionPreviewCampaigns(query);
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || 10,
      };
    },
    {
      deps: [brandUid, location.search],
      defaultFilter: {
        ascendingOrder: false,
        sortField: 'createDate',
      },
    }
  );

  const buttonColor = brandSuspended ? 'secondary' : 'primary';

  const handleModalSubmit = (payload) => {
    let authUser = null;
    const user = localStorage.getItem('user');
    if (user && JSON.parse(user)) {
      authUser = JSON.parse(user).profile.mnoNetworkId;
    }
    const data = {
      ...payload, // explanation, suspensionCategory
      authUser,
      brandUid,
      status: brandSuspended ? 'INACTIVE' : 'ACTIVE',
    };

    createBrandSuspensionRule(data);
  };

  const createBrandSuspensionRule = async (payload) => {
    try {
      setProgressMsg(
        `${suspensionRuleId ? 'Update' : 'Create'} suspension rule`
      );

      if (suspensionRuleId) {
        await updateSuspensionRule(suspensionRuleId, payload);
      } else {
        await createSuspensionRule(payload);
      }

      setTimeout(() => {
        setProgressMsg(null);
        history.push({ pathname: `/brands/${brandUid}` });
        const message = brandSuspended
          ? 'Brand Suspension Successfully Lifted'
          : 'Brand Successfully Suspended';
        toastFlashMessage(message, 'success');
      }, 1000);
    } catch (e) {
      setProgressMsg(null);
      history.push({ pathname: `/brands/${brandUid}` });
    }
  };

  const getEffectedCampaignCount = async () => {
    if (brandSuspended === null) return;
    const queryEffect = {
      ascendingOrder: false,
      brandUid,
      page: 1,
      recordsPerPage: 10,
      sortField: 'createDate',
      channelId: '10DLC',
      mnoStatus: brandSuspended
        ? MNO_STATUS_ENUM.SUSPENDED
        : MNO_STATUS_ENUM.REGISTERED,
    };
    const effectedCampaign = await getSuspensionPreviewCampaigns(queryEffect);
    setEffectedCampaignCount(effectedCampaign?.totalRecords || 0);
  };

  const getBrandRelatedData = async () => {
    try {
      const response = await fetchBrandDetail(brandUid);
      setBrandInfo(response.data);

      if (suspensionRuleId === null) {
        const rules = await getSuspensionRule({
          brandUid,
          exactMatch: true,
        });
        if (rules && rules.records.length > 0) {
          const suspensionRule = rules.records.find(
            (rule) => rule.brandUid === brandUid
          );
          if (suspensionRule) {
            setSuspensionRuleId(suspensionRule.ruleId);
          }
          const result = suspensionRule && suspensionRule.status === 'ACTIVE';
          setBrandSuspended(result);
        } else {
          setBrandSuspended(false);
        }
      }

      setLoader(false);
    } catch (error) {
      throw new Error('API Error!', error);
    }
  };

  useEffect(() => {
    getBrandRelatedData();
  }, [brandUid]);

  useEffect(() => {
    if (brandSuspended != null) {
      getEffectedCampaignCount();
    }
  }, [brandUid, brandSuspended]);

  return (
    <section className="brand-details-section page-content">
      {loader ? (
        <Loader />
      ) : (
        <Container maxWidth={false} className="brand-details-container">
          <Grid container className="top-blk">
            <Grid item xs={6} className="back-btn">
              <Link className="paragraph" to={`/brands/${brandUid}`}>
                <Flex sx={{ alignItems: 'center' }}>
                  <Box sx={{ mr: 's' }}>
                    <FontAwesomeIcon icon={faAngleLeft} size="lg" />
                  </Box>
                  back
                </Flex>
              </Link>
            </Grid>
            <Grid item xs={6} className="button-wrapper"></Grid>
          </Grid>
          <Grid container className="brand-details basic">
            <Grid item xs={12}>
              <div className="title">
                <h3 className="heading1">
                  Brand DBA Name:{' '}
                  {brandInfo.entityType.toLowerCase() === 'sole_proprietor'
                    ? 'Redacted due to PII'
                    : brandInfo.displayName
                    ? brandInfo.displayName
                    : 'N/A'}
                </h3>
                <p className="heading2">
                  Brand ID: <span style={{ fontWeight: 700 }}>{brandUid}</span>
                </p>
              </div>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div className="campaigns-listing details">
              <div className="details-heading">
                <Flex sx={{ alignItems: 'center' }}>
                  <Box sx={{ mr: 's' }}>
                    <FontAwesomeIcon icon={faBullhorn} size="lg" />
                  </Box>
                  <h3>
                    {`${table.total} ${
                      brandSuspended ? 'Suspended' : 'Active'
                    } Campaigns`}
                  </h3>
                </Flex>
              </div>
              <Table
                loading={table.loading}
                emptyState="no campaigns to view"
                handleChangePage={table.handleChangePage}
                createSortHandler={table.handleSorting}
                filter={table.filter}
                headRows={headRows}
                tableData={table.tableData.map((record) => {
                  return (
                    <SuspensionCampaignRow campaign={record} key={record.uid} />
                  );
                })}
                pagination={table.pagination}
              />
            </div>
          </Grid>
          <Grid
            item
            xs={12}
            className="flex-centered"
            style={{ justifyContent: 'center', marginTop: '40px' }}
          >
            <Button
              variant="outline"
              color={buttonColor}
              style={{ marginRight: '15px' }}
              onClick={(event) => {
                event.preventDefault();
                history.push(`/brands/${brandUid}`);
              }}
              data-testid="backToBrandDetailButton"
            >
              Cancel
            </Button>
            <Button
              color={buttonColor}
              onClick={() => setOpenBrandSuspension(true)}
              data-testid={
                brandSuspended ? 'liftSuspensionButton' : 'suspendButton'
              }
            >
              {brandSuspended ? 'Lift Suspension' : 'Suspend'}
            </Button>
          </Grid>
        </Container>
      )}
      <Backdrop
        className={classes.progressBackground}
        open={progressMsg !== null}
      >
        <Container className={classes.progressModal}>
          <Spinner />
          <Typography variant="body1" className={classes.progressText}>
            {progressMsg}
          </Typography>
        </Container>
      </Backdrop>
      <BrandSuspensionModal
        open={openBrandSuspension}
        handleClose={() => setOpenBrandSuspension(false)}
        handleSubmit={handleModalSubmit}
        brandInfo={brandInfo}
        totalCampaigns={effectedCampaignCount}
        brandSuspended={brandSuspended}
      />
    </section>
  );
};

export default withRouter(BrandSuspension);
